import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowClockwise, CheckCircle, Globe, Warning, X, XCircle } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  cancelImport,
  getImport,
  startImport,
  totalDiscovered,
  totalProcessed,
  type ImportJob,
  type ImportStatus,
} from '../api/imports';
import { ApiError } from '../api/client';

// WebsiteImportWizard — modal that starts a "import my old club website"
// job and shows progress until completion. The underlying work runs in
// the worker process (see internal/jobs/website_import.go); this
// component is purely a status UI on top of GET /imports/{id}.
//
// Lifecycle:
//   - Closed → URL input + Start button (no DB row yet).
//   - URL submitted → POST /imports returns 202; switch to live-progress
//     view + start polling.
//   - Status running → show counts; allow Cancel.
//   - Status completed/failed/cancelled → show summary + Close.
//
// The poll interval is intentionally chatty (2s) while the modal is open
// because the user is actively watching it. The bell + audit feed are
// the long-tail surfaces for users who closed the modal before the run
// finished.

export interface WebsiteImportWizardProps {
  slug: string;
  open: boolean;
  onClose: () => void;
  /**
   * Called once when a job transitions to status='completed'. The parent
   * can use this to reload its content (e.g. refresh the website list so
   * imported news posts show up immediately).
   */
  onCompleted?: (job: ImportJob) => void;
}

const POLL_MS = 2_000;

export function WebsiteImportWizard({ slug, open, onClose, onCompleted }: WebsiteImportWizardProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const [sourceURL, setSourceURL] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState<ImportJob | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const completedFiredRef = useRef(false);

  // Reset on open so a second invocation starts clean.
  useEffect(() => {
    if (open) {
      setSourceURL('');
      setSubmitting(false);
      setJob(null);
      setErrorCode(null);
      completedFiredRef.current = false;
    }
  }, [open]);

  // Poll the running job for updates. Stops automatically on terminal
  // statuses. Pauses when the modal is closed so we don't tick uselessly
  // in the background.
  useEffect(() => {
    if (!open || !job) return;
    if (isTerminal(job.status)) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const next = await getImport(token, slug, job.id);
        if (cancelled) return;
        setJob(next);
        if (next.status === 'completed' && !completedFiredRef.current) {
          completedFiredRef.current = true;
          onCompleted?.(next);
        }
        if (!isTerminal(next.status)) {
          window.setTimeout(tick, POLL_MS);
        }
      } catch (err) {
        // Transient errors (network blip) — keep polling. A persistent
        // failure surfaces when the user retries or closes + reopens.
        console.error('import poll failed', err);
        if (!cancelled) window.setTimeout(tick, POLL_MS * 2);
      }
    };
    const handle = window.setTimeout(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [open, job, slug, token, onCompleted]);

  const handleStart = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!sourceURL.trim()) return;
      setSubmitting(true);
      setErrorCode(null);
      try {
        const created = await startImport(token, slug, sourceURL.trim());
        setJob(created);
      } catch (err) {
        setErrorCode(err instanceof ApiError ? err.code : 'unknown');
      } finally {
        setSubmitting(false);
      }
    },
    [slug, sourceURL, token],
  );

  const handleCancel = useCallback(async () => {
    if (!job) return;
    try {
      await cancelImport(token, slug, job.id);
      // Optimistic — server will confirm next poll.
      setJob({ ...job, status: 'cancelled' });
    } catch (err) {
      console.error('cancel import failed', err);
    }
  }, [job, slug, token]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-background p-6 shadow-2xl outline-none"
        >
          <header className="mb-4 flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <Globe className="h-6 w-6 text-foreground/70" weight="duotone" />
              <DialogPrimitive.Title asChild>
                <h2 className="font-heading text-lg font-bold">
                  {t('websiteImport.title', { defaultValue: 'Import from existing site' })}
                </h2>
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Close asChild>
              <Button type="button" variant="ghost" size="icon" aria-label={t('common.cancel')}>
                <X className="h-5 w-5" weight="bold" />
              </Button>
            </DialogPrimitive.Close>
          </header>

          <DialogPrimitive.Description asChild>
            <p className="mb-4 text-sm text-muted-foreground">
              {t('websiteImport.description', {
                defaultValue:
                  'We will pull news posts and images from your old site and store them here. The job keeps running in the background — you can close this window and check progress in the notification bell.',
              })}
            </p>
          </DialogPrimitive.Description>

          {!job ? (
            <StartForm
              sourceURL={sourceURL}
              setSourceURL={setSourceURL}
              onSubmit={handleStart}
              submitting={submitting}
              errorCode={errorCode}
            />
          ) : (
            <RunningView job={job} onCancel={handleCancel} onClose={onClose} />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function isTerminal(status: ImportStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

function StartForm({
  sourceURL,
  setSourceURL,
  onSubmit,
  submitting,
  errorCode,
}: {
  sourceURL: string;
  setSourceURL: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  submitting: boolean;
  errorCode: string | null;
}) {
  const { t } = useTranslation('common');
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-semibold">
          {t('websiteImport.sourceUrlLabel', { defaultValue: 'Source URL' })}
        </span>
        <Input
          type="url"
          name="source_url"
          required
          autoFocus
          placeholder="https://example.com"
          value={sourceURL}
          onChange={(e) => setSourceURL(e.target.value)}
          disabled={submitting}
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
        />
        <span className="text-xs text-muted-foreground">
          {t('websiteImport.sourceUrlHint', {
            defaultValue: 'Paste the URL of your old club homepage. We will discover news via sitemap.xml and RSS feeds.',
          })}
        </span>
      </label>

      {errorCode && (
        <p className="flex items-center gap-2 rounded-pill bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
          <Warning className="h-4 w-4 shrink-0" weight="fill" />
          {errorMessage(t, errorCode)}
        </p>
      )}

      <footer className="flex items-center justify-end gap-2 pt-2">
        <DialogPrimitive.Close asChild>
          <Button type="button" variant="outline">
            {t('common.cancel')}
          </Button>
        </DialogPrimitive.Close>
        <Button type="submit" disabled={submitting || !sourceURL.trim()}>
          {submitting
            ? t('websiteImport.starting', { defaultValue: 'Starting…' })
            : t('websiteImport.startCta', { defaultValue: 'Start import' })}
        </Button>
      </footer>
    </form>
  );
}

function RunningView({
  job,
  onCancel,
  onClose,
}: {
  job: ImportJob;
  onCancel: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const news = job.progress.news ?? { discovered: 0, processed: 0, skipped: 0, failed: 0 };
  const images = job.progress.images ?? { discovered: 0, processed: 0, skipped: 0, failed: 0 };
  const overall = useMemo(() => {
    const total = totalDiscovered(job);
    const done = totalProcessed(job);
    return { total, done, pct: total === 0 ? 0 : Math.min(100, Math.round((done / total) * 100)) };
  }, [job]);

  return (
    <div className="flex flex-col gap-4">
      <StatusHeader status={job.status} />

      {job.status === 'running' && (
        <div className="space-y-2">
          <div className="h-2 overflow-hidden rounded-pill bg-muted">
            <div
              className="h-full bg-[var(--lime,#BEE034)] transition-all duration-500"
              style={{ width: `${overall.pct}%` }}
              role="progressbar"
              aria-valuenow={overall.pct}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t('websiteImport.progressOverall', {
              defaultValue: '{{done}} of {{total}} processed',
              done: overall.done,
              total: overall.total,
            })}
          </p>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-3 text-sm">
        <ProgressStat
          label={t('websiteImport.news', { defaultValue: 'News posts' })}
          discovered={news.discovered}
          processed={news.processed}
          failed={news.failed}
        />
        <ProgressStat
          label={t('websiteImport.images', { defaultValue: 'Images' })}
          discovered={images.discovered}
          processed={images.processed}
          failed={images.failed}
        />
      </dl>

      {job.status === 'failed' && job.error_message && (
        <p className="rounded-pill bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950 dark:text-red-300">
          {job.error_message}
        </p>
      )}

      <footer className="flex items-center justify-between gap-2 pt-2">
        <p className="text-xs text-muted-foreground">
          {t('websiteImport.sourceWithHost', {
            defaultValue: 'Source: {{host}}',
            host: hostOf(job.source_url),
          })}
        </p>
        <div className="flex items-center gap-2">
          {job.status === 'running' && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('websiteImport.cancelCta', { defaultValue: 'Cancel import' })}
            </Button>
          )}
          <Button type="button" onClick={onClose}>
            {job.status === 'running'
              ? t('websiteImport.closeKeepRunning', { defaultValue: 'Close (keep running)' })
              : t('common.cancel') /* "Cancel" reads as "Close" in this context */}
          </Button>
        </div>
      </footer>
    </div>
  );
}

function StatusHeader({ status }: { status: ImportStatus }) {
  const { t } = useTranslation('common');
  if (status === 'pending') {
    return (
      <div className="flex items-center gap-2 text-sm">
        <ArrowClockwise className="h-4 w-4 animate-spin" weight="bold" />
        <span>{t('websiteImport.statusPending', { defaultValue: 'Waiting for the worker to pick up…' })}</span>
      </div>
    );
  }
  if (status === 'running') {
    return (
      <div className="flex items-center gap-2 text-sm">
        <ArrowClockwise className="h-4 w-4 animate-spin" weight="bold" />
        <span>{t('websiteImport.statusRunning', { defaultValue: 'Importing…' })}</span>
      </div>
    );
  }
  if (status === 'completed') {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
        <CheckCircle className="h-5 w-5" weight="fill" />
        <span className="font-semibold">
          {t('websiteImport.statusCompleted', { defaultValue: 'Import completed' })}
        </span>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
        <XCircle className="h-5 w-5" weight="fill" />
        <span className="font-semibold">
          {t('websiteImport.statusFailed', { defaultValue: 'Import failed' })}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Warning className="h-5 w-5" weight="fill" />
      <span className="font-semibold">
        {t('websiteImport.statusCancelled', { defaultValue: 'Import cancelled' })}
      </span>
    </div>
  );
}

function ProgressStat({
  label,
  discovered,
  processed,
  failed,
}: {
  label: string;
  discovered: number;
  processed: number;
  failed: number;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="rounded-pill border border-border bg-muted/30 px-3 py-2">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="font-heading text-lg font-bold">
        {t('websiteImport.fraction', {
          defaultValue: '{{done}} / {{total}}',
          done: processed,
          total: discovered,
        })}
        {failed > 0 && (
          <span className="ml-2 text-xs text-red-600 dark:text-red-400">
            {t('websiteImport.failedCountParen', {
              defaultValue: '({{count}} failed)',
              count: failed,
            })}
          </span>
        )}
      </dd>
    </div>
  );
}

function hostOf(rawURL: string): string {
  try {
    return new URL(rawURL).host;
  } catch {
    return rawURL;
  }
}

// errorMessage maps the server's error code to a translated string.
// Literal keys (not template strings) so i18next-parser picks them up
// during the parity check.
function errorMessage(t: ReturnType<typeof useTranslation>['t'], code: string): string {
  switch (code) {
    case 'invalid_input':
      return t('websiteImport.error.invalid_input', {
        defaultValue: "That URL doesn't look right. Double-check and try again.",
      });
    case 'forbidden':
      return t('websiteImport.error.forbidden', {
        defaultValue: "You don't have permission to start an import.",
      });
    default:
      return t('websiteImport.error.unknown', {
        defaultValue: 'Could not start the import. Double-check the URL and try again.',
      });
  }
}
